import {
  AreaChart,
  Area,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
  ReferenceLine,
  ReferenceArea,
} from "recharts";
import type { EquityPoint, TimeRange } from "../../stores/backtestStore";
import { useMemo, useCallback, useState, useRef } from "react";

export interface DateRange {
  startDate: string;
  endDate: string;
}

interface EquityChartProps {
  data: EquityPoint[];
  timeRange: TimeRange;
  onTimeRangeChange: (range: TimeRange) => void;
  selectedRange?: DateRange | null;
  onRangeSelect?: (range: DateRange | null) => void;
}

const timeRanges: TimeRange[] = [
  "1M",
  "3M",
  "6M",
  "YTD",
  "1Y",
  "3Y",
  "5Y",
  "7Y",
  "10Y",
  "ALL",
];

const timeRangeDays: Record<TimeRange, number> = {
  "1M": 30,
  "3M": 90,
  "6M": 180,
  YTD: -1, // Special case: calculated dynamically
  "1Y": 365,
  "3Y": 1095,
  "5Y": 1825,
  "7Y": 2555,
  "10Y": 3650,
  ALL: Infinity,
};

function formatCurrency(value: number): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
}

function formatPercent(value: number): string {
  const sign = value >= 0 ? "+" : "";
  return `${sign}${value.toFixed(1)}%`;
}

function formatDate(dateStr: string): string {
  // Use T12:00:00 to avoid UTC midnight shifting to previous day in local timezone
  const date = new Date(dateStr + "T12:00:00");
  return date.toLocaleDateString("en-US", { month: "short", year: "2-digit" });
}

type ScaleType = "linear" | "log";

export function EquityChart({
  data,
  timeRange,
  onTimeRangeChange,
  selectedRange,
  onRangeSelect,
}: EquityChartProps) {
  const [scaleType, setScaleType] = useState<ScaleType>("linear");

  // Drag state for range selection
  const [dragLeft, setDragLeft] = useState<string | null>(null);
  const [dragRight, setDragRight] = useState<string | null>(null);
  const isDragging = useRef(false);

  const filteredData = useMemo(() => {
    if (timeRange === "ALL" || data.length === 0) return data;

    // Use the last data point's date as reference
    const lastDate = new Date(data[data.length - 1].date);
    let cutoffDate: Date;

    if (timeRange === "YTD") {
      // YTD: from January 1st of the last data point's year
      cutoffDate = new Date(lastDate.getFullYear(), 0, 1);
    } else {
      const days = timeRangeDays[timeRange];
      cutoffDate = new Date(lastDate);
      cutoffDate.setDate(cutoffDate.getDate() - days);
    }

    return data.filter((point) => new Date(point.date) >= cutoffDate);
  }, [data, timeRange]);

  // Normalize both curves to show % change from start of selected period
  const normalizedData = useMemo(() => {
    if (filteredData.length === 0) return [];

    const firstValue = filteredData[0].value;
    const firstBenchmark = filteredData[0].benchmark;

    return filteredData.map((point) => ({
      ...point,
      date: point.date,
      // Store as % change from start (e.g., +20% or -5%)
      strategy: (point.value / firstValue - 1) * 100,
      benchmark: firstBenchmark
        ? (point.benchmark! / firstBenchmark - 1) * 100
        : undefined,
      rawValue: point.value,
      rawBenchmark: point.benchmark,
      // Also store % change for tooltip
      strategyPctChange: (point.value / firstValue - 1) * 100,
      benchmarkPctChange: firstBenchmark
        ? (point.benchmark! / firstBenchmark - 1) * 100
        : undefined,
    }));
  }, [filteredData]);

  // For log scale, shift to growth-of-$100 so all values are positive
  const chartData = useMemo(() => {
    if (scaleType !== "log" || normalizedData.length === 0)
      return normalizedData;
    return normalizedData.map((point) => ({
      ...point,
      strategy: point.strategy + 100,
      benchmark:
        point.benchmark !== undefined ? point.benchmark + 100 : undefined,
    }));
  }, [scaleType, normalizedData]);

  interface ChartPayload {
    rawValue: number;
    rawBenchmark?: number;
    strategyPctChange: number;
    benchmarkPctChange?: number;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const renderCustomTooltip = useCallback((props: any) => {
    const { active, payload, label } = props;
    if (active && payload && payload.length) {
      const strategyData = payload.find(
        (p: { dataKey?: string }) => p.dataKey === "strategy",
      );
      const benchmarkData = payload.find(
        (p: { dataKey?: string }) => p.dataKey === "benchmark",
      );
      const strategyPayload = strategyData?.payload as ChartPayload | undefined;
      const benchmarkPayload = benchmarkData?.payload as
        | ChartPayload
        | undefined;

      return (
        <div className="bg-surface-800 border border-surface-600 rounded-lg p-3 shadow-lg">
          <p className="text-surface-300 text-sm mb-2">{label}</p>
          {strategyPayload && (
            <div className="flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-emerald-500"></span>
              <span className="text-emerald-400 text-sm">Strategy:</span>
              <span className="text-surface-200 text-sm font-medium">
                {formatCurrency(strategyPayload.rawValue)}
              </span>
              <span
                className={`text-sm font-medium ${strategyPayload.strategyPctChange >= 0 ? "text-emerald-400" : "text-red-400"}`}
              >
                ({formatPercent(strategyPayload.strategyPctChange)})
              </span>
            </div>
          )}
          {benchmarkPayload && benchmarkPayload.rawBenchmark && (
            <div className="flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-pink-400"></span>
              <span className="text-pink-400 text-sm">SPY:</span>
              <span className="text-surface-300 text-sm">
                {formatCurrency(benchmarkPayload.rawBenchmark)}
              </span>
              <span
                className={`text-sm ${benchmarkPayload.benchmarkPctChange && benchmarkPayload.benchmarkPctChange >= 0 ? "text-emerald-400/80" : "text-red-400/80"}`}
              >
                ({formatPercent(benchmarkPayload.benchmarkPctChange ?? 0)})
              </span>
            </div>
          )}
        </div>
      );
    }
    return null;
  }, []);

  return (
    <div className="glass-card p-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
        <h3 className="text-lg font-semibold text-surface-100">Equity Curve</h3>
        <div className="flex items-center gap-3 overflow-x-auto no-scrollbar pb-1 -mb-1">
          <div className="flex items-center bg-surface-800 rounded-lg p-0.5 shrink-0">
            <button
              onClick={() => setScaleType("linear")}
              className={`px-2.5 py-1 text-xs font-medium rounded-md transition-colors ${
                scaleType === "linear"
                  ? "bg-surface-600 text-surface-100"
                  : "text-surface-400 hover:text-surface-200"
              }`}
            >
              Linear
            </button>
            <button
              onClick={() => setScaleType("log")}
              className={`px-2.5 py-1 text-xs font-medium rounded-md transition-colors ${
                scaleType === "log"
                  ? "bg-surface-600 text-surface-100"
                  : "text-surface-400 hover:text-surface-200"
              }`}
            >
              Log
            </button>
          </div>
          <div className="flex gap-2">
            {timeRanges.map((range) => (
              <button
                key={range}
                onClick={() => onTimeRangeChange(range)}
                className={`time-range-btn ${
                  timeRange === range
                    ? "time-range-btn-active"
                    : "time-range-btn-inactive"
                }`}
              >
                {range}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="h-80">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart
            data={chartData}
            onMouseDown={(e) => {
              if (e?.activeLabel) {
                setDragLeft(e.activeLabel as string);
                setDragRight(null);
                isDragging.current = true;
              }
            }}
            onMouseMove={(e) => {
              if (isDragging.current && e?.activeLabel) {
                setDragRight(e.activeLabel as string);
              }
            }}
            onMouseUp={() => {
              if (isDragging.current && dragLeft && dragRight && dragLeft !== dragRight) {
                const [left, right] = [dragLeft, dragRight].sort();
                onRangeSelect?.({ startDate: left, endDate: right });
              } else if (isDragging.current && !dragRight) {
                // Click without drag = clear selection
                onRangeSelect?.(null);
              }
              setDragLeft(null);
              setDragRight(null);
              isDragging.current = false;
            }}
            style={{ cursor: onRangeSelect ? "crosshair" : undefined }}
          >
            <defs>
              {/* Green gradient for strategy */}
              <linearGradient id="strategyGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#10b981" stopOpacity={0.4} />
                <stop offset="50%" stopColor="#10b981" stopOpacity={0.15} />
                <stop offset="100%" stopColor="#10b981" stopOpacity={0.02} />
              </linearGradient>
            </defs>
            <CartesianGrid
              strokeDasharray="3 3"
              stroke="#27272a"
              vertical={false}
            />
            <XAxis
              dataKey="date"
              stroke="#52525b"
              fontSize={11}
              tickFormatter={formatDate}
              interval="preserveStartEnd"
              axisLine={{ stroke: "#3f3f46" }}
              tickLine={{ stroke: "#3f3f46" }}
            />
            {/* Hidden left Y-axis for data binding */}
            <YAxis
              yAxisId="left"
              hide={true}
              domain={["auto", "auto"]}
              scale={scaleType === "log" ? "log" : "auto"}
              allowDataOverflow={scaleType === "log"}
            />
            {/* Right Y-axis for percentage display */}
            <YAxis
              yAxisId="right"
              orientation="right"
              stroke="#71717a"
              fontSize={11}
              scale={scaleType === "log" ? "log" : "auto"}
              allowDataOverflow={scaleType === "log"}
              tickFormatter={
                scaleType === "log"
                  ? (value: number) => `$${value.toFixed(0)}`
                  : (value: number) =>
                      `${value >= 0 ? "+" : ""}${value.toFixed(0)}%`
              }
              domain={["auto", "auto"]}
              axisLine={false}
              tickLine={false}
              width={50}
            />
            <ReferenceLine
              yAxisId="left"
              y={scaleType === "log" ? 100 : 0}
              stroke="#52525b"
              strokeWidth={1}
              strokeDasharray="3 3"
            />
            <Tooltip content={renderCustomTooltip} />
            <Legend
              wrapperStyle={{ paddingTop: 16 }}
              formatter={(value) => (
                <span
                  className="text-sm"
                  style={{
                    color: value === "strategy" ? "#10b981" : "#f472b6",
                  }}
                >
                  {value === "strategy" ? "● Strategy" : "● SPY"}
                </span>
              )}
            />
            {/* Strategy area with green fill */}
            <Area
              yAxisId="left"
              type="monotone"
              dataKey="strategy"
              stroke="#10b981"
              strokeWidth={2}
              fill="url(#strategyGradient)"
              name="strategy"
              dot={false}
              activeDot={{
                r: 4,
                fill: "#10b981",
                stroke: "#fff",
                strokeWidth: 2,
              }}
            />
            {/* SPY benchmark in pink/red */}
            <Line
              yAxisId="left"
              type="monotone"
              dataKey="benchmark"
              stroke="#f472b6"
              strokeWidth={1.5}
              dot={false}
              name="benchmark"
              activeDot={{
                r: 3,
                fill: "#f472b6",
                stroke: "#fff",
                strokeWidth: 1,
              }}
            />
            {/* Drag selection overlay */}
            {isDragging.current && dragLeft && dragRight && (
              <ReferenceArea
                yAxisId="left"
                x1={dragLeft}
                x2={dragRight}
                fill="#22d3ee"
                fillOpacity={0.15}
                stroke="#22d3ee"
                strokeOpacity={0.4}
              />
            )}
            {/* Persistent selected range */}
            {selectedRange && !isDragging.current && (
              <ReferenceArea
                yAxisId="left"
                x1={selectedRange.startDate}
                x2={selectedRange.endDate}
                fill="#22d3ee"
                fillOpacity={0.12}
                stroke="#22d3ee"
                strokeOpacity={0.3}
              />
            )}
          </AreaChart>
        </ResponsiveContainer>
      </div>

      {/* Performance summary at bottom */}
      {normalizedData.length > 0 && (
        <div className="flex items-center justify-center gap-8 mt-4 pt-4 border-t border-surface-700/50">
          <div className="flex items-center gap-2">
            <span className="w-3 h-3 rounded-full bg-emerald-500"></span>
            <span className="text-surface-400 text-sm">Strategy:</span>
            <span
              className={`font-semibold ${normalizedData[normalizedData.length - 1].strategy >= 0 ? "text-emerald-400" : "text-red-400"}`}
            >
              {formatPercent(
                normalizedData[normalizedData.length - 1].strategy,
              )}
            </span>
          </div>
          {normalizedData[normalizedData.length - 1].benchmark !==
            undefined && (
            <div className="flex items-center gap-2">
              <span className="w-3 h-3 rounded-full bg-pink-400"></span>
              <span className="text-surface-400 text-sm">SPY:</span>
              <span
                className={`font-semibold ${normalizedData[normalizedData.length - 1].benchmark! >= 0 ? "text-emerald-400" : "text-red-400"}`}
              >
                {formatPercent(
                  normalizedData[normalizedData.length - 1].benchmark!,
                )}
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
